import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardApi, jobsApi, unifiedApi, systemApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  UserCheck,
  MapPin,
  Wrench,
  CheckSquare,
  ArrowRight,
  Clock,
  ClipboardList,
  PlayCircle,
  FileSpreadsheet,
  Receipt,
  AlertTriangle,
  TrendingUp,
  PoundSterling,
  Shield,
  Calendar,
  Bell,
  BarChart3,
  Download,
  RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

const WORKFLOW_STAGES = [
  { id: 'enquiry', label: 'Enquiry', color: 'bg-gray-500' },
  { id: 'quote_sent', label: 'Quote Sent', color: 'bg-blue-500' },
  { id: 'quote_accepted', label: 'Accepted', color: 'bg-green-500' },
  { id: 'allocated_to_surveyor', label: 'Allocated', color: 'bg-indigo-500' },
  { id: 'survey_scheduled', label: 'Scheduled', color: 'bg-cyan-500' },
  { id: 'survey_in_progress', label: 'In Progress', color: 'bg-amber-500' },
  { id: 'survey_completed', label: 'Completed', color: 'bg-emerald-500' },
];

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'emerald', onClick }) => (
  <Card 
    className={`metric-card ${onClick ? 'cursor-pointer hover:shadow-md' : ''}`} 
    onClick={onClick}
    data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-semibold text-stone-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {value}
          </p>
          {subtitle && <p className="text-xs text-stone-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`h-5 w-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Critical Actions Panel Component
const CriticalActionsPanel = ({ stats, navigate }) => {
  const criticalActions = [
    { 
      label: 'Quotes Need Sending', 
      count: stats?.quotes_awaiting_send || 0, 
      color: 'amber',
      path: '/phs-members?tab=quotes',
      icon: FileSpreadsheet
    },
    { 
      label: 'Accepted Awaiting Payment', 
      count: stats?.accepted_unpaid || 0, 
      color: 'orange',
      path: '/finance?tab=invoices',
      icon: Receipt
    },
    { 
      label: 'Paid Awaiting Scheduling', 
      count: stats?.paid_not_scheduled || 0, 
      color: 'blue',
      path: '/dispatch',
      icon: Calendar
    },
    { 
      label: 'Overdue Contractor Response', 
      count: stats?.overdue_contractor || 0, 
      color: 'red',
      path: '/contractors',
      icon: AlertTriangle
    },
    { 
      label: 'Completed Not Invoiced', 
      count: stats?.completed_not_invoiced || 0, 
      color: 'purple',
      path: '/finance?tab=exceptions',
      icon: Receipt
    },
    { 
      label: 'SLA Breaches', 
      count: stats?.sla_breaches || 0, 
      color: 'red',
      path: '/operations',
      icon: Shield
    },
  ];

  const hasActions = criticalActions.some(a => a.count > 0);

  if (!hasActions) return null;

  return (
    <Card className="border-red-200 bg-gradient-to-r from-red-50 to-amber-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-red-800">
          <AlertTriangle className="h-5 w-5" />
          Critical Actions Required
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {criticalActions.filter(a => a.count > 0).map((action) => {
            const Icon = action.icon;
            return (
              <div 
                key={action.label}
                onClick={() => navigate(action.path)}
                className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-105 
                  ${action.color === 'red' ? 'bg-red-100 hover:bg-red-200 border border-red-200' : ''}
                  ${action.color === 'amber' ? 'bg-amber-100 hover:bg-amber-200 border border-amber-200' : ''}
                  ${action.color === 'orange' ? 'bg-orange-100 hover:bg-orange-200 border border-orange-200' : ''}
                  ${action.color === 'blue' ? 'bg-blue-100 hover:bg-blue-200 border border-blue-200' : ''}
                  ${action.color === 'purple' ? 'bg-purple-100 hover:bg-purple-200 border border-purple-200' : ''}
                `}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`h-4 w-4 
                    ${action.color === 'red' ? 'text-red-600' : ''}
                    ${action.color === 'amber' ? 'text-amber-600' : ''}
                    ${action.color === 'orange' ? 'text-orange-600' : ''}
                    ${action.color === 'blue' ? 'text-blue-600' : ''}
                    ${action.color === 'purple' ? 'text-purple-600' : ''}
                  `} />
                  <span className={`text-xl font-bold
                    ${action.color === 'red' ? 'text-red-700' : ''}
                    ${action.color === 'amber' ? 'text-amber-700' : ''}
                    ${action.color === 'orange' ? 'text-orange-700' : ''}
                    ${action.color === 'blue' ? 'text-blue-700' : ''}
                    ${action.color === 'purple' ? 'text-purple-700' : ''}
                  `}>{action.count}</span>
                </div>
                <p className={`text-xs font-medium
                  ${action.color === 'red' ? 'text-red-600' : ''}
                  ${action.color === 'amber' ? 'text-amber-600' : ''}
                  ${action.color === 'orange' ? 'text-orange-600' : ''}
                  ${action.color === 'blue' ? 'text-blue-600' : ''}
                  ${action.color === 'purple' ? 'text-purple-600' : ''}
                `}>{action.label}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [unifiedStats, setUnifiedStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [myJobs, setMyJobs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, activityData, unifiedData, analyticsData] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getRecentActivity(),
        dashboardApi.getUnifiedStats().catch(() => null),
        dashboardApi.getAnalytics().catch(() => null),
      ]);
      setStats(statsData);
      setRecentActivity(activityData);
      setUnifiedStats(unifiedData);
      setAnalytics(analyticsData);

      // For surveyors, also load their assigned jobs
      if (user?.role === 'surveyor') {
        const jobs = await jobsApi.getAll();
        setMyJobs(jobs.filter(j => !['closed', 'report_sent'].includes(j.stage)));
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const result = await systemApi.runOverdueCheck();
      toast.success(`Sent ${result.quote_reminders || 0} reminders, ${result.amber_alerts || 0} alerts`);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  const isSurveyor = user?.role === 'surveyor';

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Command Centre</h1>
          <p className="text-sm text-stone-500 mt-1">
            {isSurveyor ? 'Your assigned jobs and tasks' : 'Live operational control centre'}
          </p>
        </div>
        {!isSurveyor && (
          <div className="flex gap-2">
            <Link to="/dispatch">
              <Button variant="outline" className="border-[#0F766E] text-[#0F766E]">
                <Wrench className="h-4 w-4 mr-2" /> Dispatch
              </Button>
            </Link>
            <Link to="/jobs">
              <Button className="bg-[#0F766E] hover:bg-[#115E59]">
                <Briefcase className="h-4 w-4 mr-2" /> View All Jobs
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Critical Actions Panel - Only for Ops */}
      {!isSurveyor && unifiedStats && (
        <CriticalActionsPanel stats={unifiedStats} navigate={navigate} />
      )}

      {/* Surveyor Quick Actions */}
      {isSurveyor && myJobs.length > 0 && (
        <Card className="bg-gradient-to-r from-emerald-900 to-emerald-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Your Active Jobs</h2>
                <p className="text-emerald-200 text-sm mt-1">
                  {myJobs.length} job{myJobs.length !== 1 ? 's' : ''} assigned to you
                </p>
              </div>
              <Link to="/jobs">
                <Button variant="secondary" className="bg-white text-emerald-900 hover:bg-emerald-50">
                  <ClipboardList className="h-4 w-4 mr-2" /> View Jobs
                </Button>
              </Link>
            </div>

            {/* Quick job cards for surveyor */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {myJobs.slice(0, 3).map((job) => (
                <div 
                  key={job.id} 
                  className="bg-white/10 rounded-lg p-4 cursor-pointer hover:bg-white/20 transition-colors"
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  data-testid={`my-job-card-${job.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-emerald-200">{job.job_number}</span>
                    <Badge className="bg-white/20 text-white text-xs capitalize">
                      {job.stage?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="font-medium text-sm truncate">{job.title}</p>
                  <p className="text-xs text-emerald-200 truncate">
                    {job.park_name || job.property_address}{job.plot_number ? ` Plot ${job.plot_number}` : ''} • {job.property_postcode}
                  </p>
                  {job.assigned_contractor_name && (
                    <p className="text-xs text-white/80 mt-1 truncate">{job.assigned_contractor_name}</p>
                  )}
                  {(job.stage === 'survey_scheduled' || job.stage === 'survey_in_progress') && (
                    <Button size="sm" className="mt-3 w-full bg-white text-emerald-900 hover:bg-emerald-50">
                      <PlayCircle className="h-4 w-4 mr-1" /> Start Survey
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unified Portal Stats (Admin view) */}
      {!isSurveyor && unifiedStats && (
        <div className="bg-gradient-to-r from-[#1F7A78] to-[#005456] rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
          <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center gap-2">
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
            System Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4">
            <div 
              className="bg-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer hover:bg-white/20 transition-colors"
              onClick={() => navigate('/jobs?priority=Urgent')}
            >
              <p className="text-lg sm:text-2xl font-bold">{unifiedStats.urgent_jobs || 0}</p>
              <p className="text-xs sm:text-sm text-white/80">Urgent Jobs</p>
            </div>
            <div 
              className="bg-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer hover:bg-white/20 transition-colors"
              onClick={() => navigate('/contractors')}
            >
              <p className="text-lg sm:text-2xl font-bold">{unifiedStats.awaiting_contractor || 0}</p>
              <p className="text-xs sm:text-sm text-white/80">Awaiting Contractor</p>
            </div>
            <div 
              className="bg-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer hover:bg-white/20 transition-colors"
              onClick={() => navigate('/calendar')}
            >
              <p className="text-lg sm:text-2xl font-bold">{unifiedStats.scheduled_this_week || 0}</p>
              <p className="text-xs sm:text-sm text-white/80">Visits This Week</p>
            </div>
            <div 
              className="bg-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer hover:bg-white/20 transition-colors"
              onClick={() => navigate('/jobs?overdue=true')}
            >
              <p className="text-lg sm:text-2xl font-bold">{unifiedStats.overdue_jobs || 0}</p>
              <p className="text-xs sm:text-sm text-white/80">Overdue Jobs</p>
            </div>
          </div>
          
          {/* Recent Client Portal Submissions */}
          {unifiedStats.recent_client_submissions?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-white/80 mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Recent Client Portal Submissions
              </h3>
              <div className="space-y-2 max-h-60 overflow-auto">
                {unifiedStats.recent_client_submissions.slice(0, 5).map(sub => (
                  <div 
                    key={sub.id} 
                    className="bg-white/10 rounded-lg p-3 cursor-pointer hover:bg-white/20 transition-colors"
                    onClick={() => navigate(`/fm/jobs/${sub.id}`)}
                    data-testid={`portal-submission-${sub.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-sm">{sub.job_reference}</p>
                      <Badge className={sub.intake_mode === 'insurance_claim' ? 'bg-blue-500/30 text-blue-100' : 'bg-amber-500/30 text-amber-100'}>
                        {sub.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-white/70">
                      {sub.intake_mode === 'insurance_claim' ? 'Insurance Claim' : 'Maintenance'} • {sub.issue_type || sub.job_type_label || sub.job_type}
                    </p>
                    <p className="text-xs text-white/90 mt-1">
                      {sub.site_name || sub.park_name || 'Unknown Site'}{sub.plot_number ? ` • Plot ${sub.plot_number}` : ''}{sub.postcode ? ` • ${sub.postcode}` : ''}
                    </p>
                    {sub.assigned_contractor_name && (
                      <p className="text-xs text-emerald-300 mt-0.5 font-medium">
                        {sub.assigned_contractor_name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Job Pipeline (Admin/Manager view) */}
      {!isSurveyor && stats?.jobs?.by_stage && (
        <div>
          <h2 className="text-xs sm:text-sm font-medium text-stone-500 uppercase tracking-wider mb-2 sm:mb-3">Job Pipeline</h2>
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {WORKFLOW_STAGES.map((stage) => {
              const count = stats.jobs.by_stage[stage.id] || 0;
              return (
                <Card 
                  key={stage.id} 
                  className="min-w-[100px] sm:min-w-[140px] cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/jobs?stage=${stage.id}`)}
                >
                  <CardContent className="p-2 sm:p-4 text-center">
                    <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${stage.color} mx-auto mb-1 sm:mb-2`} />
                    <p className="text-lg sm:text-2xl font-semibold">{count}</p>
                    <p className="text-[10px] sm:text-xs text-stone-500">{stage.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Overview Metrics */}
      <div>
        <h2 className="text-xs sm:text-sm font-medium text-stone-500 uppercase tracking-wider mb-2 sm:mb-3">Overview</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
          <MetricCard 
            title="Total Jobs" 
            value={stats?.jobs?.total || 0}
            subtitle={`${stats?.jobs?.active || 0} active`}
            icon={Briefcase}
            color="emerald"
            onClick={() => navigate('/jobs')}
          />
          <MetricCard 
            title="Leads" 
            value={stats?.leads?.total || 0}
            icon={Users}
            color="blue"
            onClick={() => navigate('/leads')}
          />
          <MetricCard 
            title="Clients" 
            value={stats?.clients?.total || 0}
            icon={UserCheck}
            color="purple"
            onClick={() => navigate('/clients')}
          />
          <MetricCard 
            title="Parks" 
            value={stats?.parks?.total || 0}
            icon={MapPin}
            color="green"
            onClick={() => navigate('/parks')}
          />
          <MetricCard 
            title="Contractors" 
            value={stats?.contractors?.total || 0}
            icon={Wrench}
            color="amber"
            onClick={() => navigate('/contractors')}
          />
          <MetricCard 
            title="Pending Tasks" 
            value={stats?.tasks?.pending || 0}
            icon={CheckSquare}
            color="sky"
            onClick={() => navigate('/tasks')}
          />
        </div>
      </div>

      {/* Financial KPIs */}
      {!isSurveyor && (
        <div>
          <h2 className="text-xs sm:text-sm font-medium text-stone-500 uppercase tracking-wider mb-2 sm:mb-3">Financial Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/quotes')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold">{stats?.quotes?.pending || 0}</p>
                    <p className="text-xs text-stone-500">Pending Quotes</p>
                    <p className="text-sm font-medium text-blue-600">
                      £{((stats?.quotes?.value_pending || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/quotes')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold">{stats?.quotes?.accepted || 0}</p>
                    <p className="text-xs text-stone-500">Accepted Quotes</p>
                    <p className="text-sm font-medium text-green-600">
                      £{((stats?.quotes?.value_accepted || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/invoices')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Receipt className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold">{stats?.invoices?.unpaid || 0}</p>
                    <p className="text-xs text-stone-500">Outstanding</p>
                    <p className="text-sm font-medium text-amber-600">
                      £{((stats?.invoices?.value_outstanding || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <PoundSterling className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold">{stats?.invoices?.paid || 0}</p>
                    <p className="text-xs text-stone-500">Paid</p>
                    <p className="text-sm font-medium text-emerald-600">
                      £{((stats?.invoices?.value_collected || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Attention Required (Stale Jobs & Overdue Tasks) */}
      {!isSurveyor && ((stats?.jobs?.stale_amber > 0) || (stats?.jobs?.stale_red > 0) || (stats?.tasks?.overdue > 0)) && (
        <div>
          <h2 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">
            <AlertTriangle className="h-4 w-4 inline mr-2 text-amber-500" />
            Attention Required
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats?.jobs?.stale_red > 0 && (
              <Card className="border-red-200 bg-red-50 cursor-pointer hover:shadow-md" onClick={() => navigate('/jobs')}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-red-700">{stats.jobs.stale_red}</p>
                      <p className="text-sm text-red-600">Critical - 14+ days inactive</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {stats?.jobs?.stale_amber > 0 && (
              <Card className="border-amber-200 bg-amber-50 cursor-pointer hover:shadow-md" onClick={() => navigate('/jobs')}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-amber-700">{stats.jobs.stale_amber}</p>
                      <p className="text-sm text-amber-600">Warning - 7+ days inactive</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {stats?.tasks?.overdue > 0 && (
              <Card className="border-orange-200 bg-orange-50 cursor-pointer hover:shadow-md" onClick={() => navigate('/tasks')}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <CheckSquare className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-orange-700">{stats.tasks.overdue}</p>
                      <p className="text-sm text-orange-600">Overdue Tasks</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Surveyor Stats (Admin/Manager) */}
      {!isSurveyor && stats?.surveyors && (
        <div>
          <h2 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">Team</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-semibold text-emerald-900">{stats.surveyors.total}</p>
                <p className="text-sm text-stone-500">Total Surveyors</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-semibold text-green-600">{stats.surveyors.available}</p>
                <p className="text-sm text-stone-500">Available</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Analytics Charts (Admin/Manager) */}
      {!isSurveyor && analytics && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3 gap-2">
            <h2 className="text-xs sm:text-sm font-medium text-stone-500 uppercase tracking-wider flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics & Insights
            </h2>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSendReminders}
              disabled={sendingReminders}
              className="gap-2 text-xs sm:text-sm"
            >
              {sendingReminders ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
              <span className="hidden sm:inline">Send Overdue Reminders</span>
              <span className="sm:hidden">Reminders</span>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Monthly Job Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Job Trend (Last 6 Months)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.jobs?.monthly_trend || []}>
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1F7A78" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Quote Conversion */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Quote Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Accepted', value: analytics.quotes?.accepted || 0, color: '#10b981' },
                          { name: 'Pending', value: analytics.quotes?.pending || 0, color: '#f59e0b' },
                          { name: 'Rejected', value: analytics.quotes?.rejected || 0, color: '#ef4444' },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {[
                          { name: 'Accepted', value: analytics.quotes?.accepted || 0, color: '#10b981' },
                          { name: 'Pending', value: analytics.quotes?.pending || 0, color: '#f59e0b' },
                          { name: 'Rejected', value: analytics.quotes?.rejected || 0, color: '#ef4444' },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value} quotes`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-2">
                  <p className="text-2xl font-bold text-[#1F7A78]">{analytics.quotes?.conversion_rate || 0}%</p>
                  <p className="text-xs text-stone-500">Conversion Rate</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Jobs by Stage */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Jobs by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={Object.entries(analytics.jobs?.by_stage || {}).map(([stage, count]) => ({
                        stage: stage.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        count
                      })).slice(0, 8)}
                      layout="vertical"
                    >
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="stage" type="category" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0dbdad" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Revenue Overview */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Revenue Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <p className="text-2xl font-bold text-green-600">
                      £{(analytics.quotes?.total_value || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-green-700">Accepted Quotes</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600">
                      £{(analytics.invoices?.total_paid || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-blue-700">Invoices Paid</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl">
                    <p className="text-2xl font-bold text-amber-600">
                      £{(analytics.invoices?.total_outstanding || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-amber-700">Outstanding</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-600">
                      {analytics.jobs?.avg_completion_days || 0} days
                    </p>
                    <p className="text-xs text-slate-700">Avg. Completion</p>
                  </div>
                </div>
                
                {/* Contractor Performance */}
                {analytics.contractors?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-medium text-stone-500 mb-2">Top Contractors</p>
                    <div className="space-y-2">
                      {analytics.contractors.slice(0, 3).map((c, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="truncate">{c._id || 'Unknown'}</span>
                          <span className="text-stone-500">
                            {c.completed}/{c.total_jobs} jobs
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Jobs */}
        <Card data-testid="recent-jobs-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">Recent Jobs</CardTitle>
              <Link to="/jobs">
                <Button variant="ghost" size="sm" className="text-xs">
                  View all <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {recentActivity?.recent_jobs?.length > 0 ? (
                recentActivity.recent_jobs.slice(0, 5).map((job) => (
                  <div 
                    key={job.id} 
                    className="flex items-start justify-between py-2 border-b border-stone-100 last:border-0 cursor-pointer hover:bg-stone-50 -mx-2 px-2 rounded transition-colors"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    data-testid={`recent-job-${job.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-stone-900 truncate">{job.title}</p>
                      </div>
                      <p className="text-xs text-stone-500">
                        {job.job_number} • {job.park_name || job.property_address || ''}{job.plot_number ? ` Plot ${job.plot_number}` : ''} • {job.property_postcode}
                      </p>
                      {job.assigned_contractor_name && (
                        <p className="text-xs text-[#1F7A78] font-medium mt-0.5">
                          {job.assigned_contractor_name}
                        </p>
                      )}
                    </div>
                    <Badge className="capitalize text-xs bg-stone-100 text-stone-700 ml-2 flex-shrink-0">
                      {job.stage?.replace('_', ' ')}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-stone-500 text-center py-4">No recent jobs</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Actions */}
        <Card data-testid="recent-actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {recentActivity?.recent_actions?.length > 0 ? (
                recentActivity.recent_actions.slice(0, 5).map((action) => (
                  <div key={action.id} className="flex items-start gap-3 py-2 border-b border-stone-100 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-4 w-4 text-stone-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-stone-900 truncate">{action.description}</p>
                      <p className="text-xs text-stone-500">
                        {action.performed_by_name} • {action.created_at && format(new Date(action.created_at), 'MMM d, HH:mm')}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-stone-500 text-center py-4">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
